import React, { useEffect, useState } from 'react';
import {
  Box, Chip, CircularProgress, Dialog, DialogContent, DialogTitle, Divider,
  Grid, Typography,
} from '@mui/material';
import { branchProofUrl } from '../../services/branchApi';

/* Detail view (the "eye" icon) for a deposit/withdraw: shows the full payment
   info — which payment system/account was assigned/used — plus the proof
   screenshot, so the approver can verify before approving. */
const Row = ({ label, value }: { label: string; value: any }) =>
  value ? (
    <Grid item xs={6}>
      <Typography variant="caption" color="text.secondary">{label}</Typography>
      <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>{value}</Typography>
    </Grid>
  ) : null;

export default function TxnDetailModal({
  open, onClose, kind, row,
}: {
  open: boolean;
  onClose: () => void;
  kind: 'deposit' | 'withdraw';
  row: any | null;
}) {
  const [proof, setProof] = useState<string>('');
  const [payoutProof, setPayoutProof] = useState<string>('');
  const [loadingProof, setLoadingProof] = useState(false);
  const [proofErr, setProofErr] = useState('');

  const id = kind === 'deposit' ? row?.deposit_id : row?.id;
  const path = kind === 'deposit' ? 'deposits' : 'withdrawals';

  useEffect(() => {
    setProof(''); setPayoutProof(''); setProofErr('');
    if (!open || !row) return;
    const urls: string[] = [];
    if (row.has_screenshot) {
      setLoadingProof(true);
      branchProofUrl(path, id)
        .then((u) => { urls.push(u); setProof(u); })
        .catch(() => setProofErr('Could not load proof'))
        .finally(() => setLoadingProof(false));
    }
    // agent's payout proof (withdrawals only)
    if (kind === 'withdraw' && row.has_payout_proof) {
      branchProofUrl('withdrawals', id, 'payout-proof')
        .then((u) => { urls.push(u); setPayoutProof(u); })
        .catch(() => {});
    }
    return () => { urls.forEach((u) => URL.revokeObjectURL(u)); };
    // eslint-disable-next-line
  }, [open, id]);

  if (!row) return null;
  const method = row.upi_id ? 'UPI' : (row.bank_name ? 'BANK / IMPS / NEFT' : '—');

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{kind === 'deposit' ? 'Deposit' : 'Withdrawal'} details</DialogTitle>
      <DialogContent dividers>
        <Grid container spacing={2}>
          <Row label="User" value={row.username} />
          <Row label="Amount" value={`₹${Number(row.amount).toLocaleString()}`} />
          <Row label="Status" value={<Chip size="small" label={row.status} />} />
          <Row label={kind === 'deposit' ? 'Transaction / UTR' : 'Withdraw ref'} value={row.transaction_id} />
          <Row label="Date" value={(row.created_at || row.date) ? new Date(row.created_at || row.date).toLocaleString() : null} />
        </Grid>

        <Divider sx={{ my: 2 }} />
        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          Payment account ({method})
        </Typography>
        <Grid container spacing={2}>
          <Row label="UPI ID" value={row.upi_id} />
          <Row label="Bank Name" value={row.bank_name} />
          <Row label="Account Number" value={row.account_number} />
          <Row label="IFSC" value={row.ifsc_code} />
          <Row label="Account Holder" value={row.account_holder_name} />
          {!row.upi_id && !row.bank_name && (
            <Grid item xs={12}><Typography variant="body2" color="text.secondary">No account info recorded.</Typography></Grid>
          )}
        </Grid>

        <Divider sx={{ my: 2 }} />
        <Typography variant="subtitle2" sx={{ mb: 1 }}>Proof screenshot</Typography>
        {!row.has_screenshot ? (
          <Typography variant="body2" color="text.secondary">No screenshot uploaded.</Typography>
        ) : loadingProof ? (
          <Box sx={{ textAlign: 'center', py: 2 }}><CircularProgress size={22} /></Box>
        ) : proofErr ? (
          <Typography variant="body2" color="error">{proofErr}</Typography>
        ) : proof ? (
          <Box sx={{ textAlign: 'center' }}>
            <a href={proof} target="_blank" rel="noreferrer">
              <img src={proof} alt="proof" style={{ maxWidth: '100%', maxHeight: 360, borderRadius: 8 }} />
            </a>
            <Typography variant="caption" color="text.secondary" display="block">click to open full size</Typography>
          </Box>
        ) : null}

        {kind === 'withdraw' && (row.remark || row.action_by) && (
          <>
            <Divider sx={{ my: 2 }} />
            <Grid container spacing={2}>
              <Row label="Handled by" value={row.action_by} />
              <Row label="Action date" value={row.handled_at ? new Date(row.handled_at).toLocaleString() : null} />
              <Row label="Remark" value={row.remark} />
            </Grid>
          </>
        )}

        {kind === 'withdraw' && payoutProof && (
          <>
            <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle2" sx={{ mb: 1 }}>Agent payout proof</Typography>
            <Box sx={{ textAlign: 'center' }}>
              <a href={payoutProof} target="_blank" rel="noreferrer">
                <img src={payoutProof} alt="payout proof" style={{ maxWidth: '100%', maxHeight: 360, borderRadius: 8 }} />
              </a>
            </Box>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
